import React from 'react';
import { connect } from 'react-redux';
import { match } from 'react-router-dom';
import moment from 'moment';
import { HttpStateLine } from 'state-line';

import Paper from 'xgimi-ui/mui/material/Paper';
import { green, red } from 'xgimi-ui/mui/material/colors';
import Fab from 'xgimi-ui/mui/material/Fab';
import CircularProgress from 'xgimi-ui/mui/material/CircularProgress';
import Container from 'xgimi-ui/Container';
import Button from 'xgimi-ui/Button';
import Info, { InfoItem, InfoItemType } from 'xgimi-ui/Info';
import InfoSelect, { InfoSelectOption } from 'xgimi-ui/Info/Select';
import Table from 'xgimi-ui/Table';
import Dialog from 'xgimi-ui/Dialog';
import { getUrlParams } from 'xgimi-utils/url';
import InfoFile, { InfoFileType } from 'xgimi-ui/Info/File';
import Icon, { icons } from 'xgimi-ui/Icon';

import StoreComponent, { mapStateToProps } from '../../../../appComponents/Store';
import Accordion from '../../../../components/Accordion';
import Confirm from '../../../../components/Confirm';
import Assign from './Assign';
import Reply from './Reply';
import Remark from './Remark';

import s from './.module.scss';
import Api from '../../../../api';
import Type from '../../../Feedback/Type';

interface Props {
  match: match<{
    // 反馈编号
    feedbackNo: string;
  }>;
}

interface State {
  // 是否加载
  isLoading: boolean;
  // 详情数据
  detailData: Record<string, any>;
  // 分配弹窗
  isOpenAssign: boolean;
  // 回复弹窗
  isOpenReply: boolean;
  // 备注弹窗
  isOpenRemark: boolean;
  // 完结弹窗
  isFinish: boolean;
  // 类型选择弹框
  isOpenType: boolean;
  // 当前回复
  currentReply: Record<string, any>;
  // 当前备注
  currentRemark: Record<string, any>;
}

/**
 * 企业微信端意见反馈详情
 */
class Component extends StoreComponent<Props, State> {
  state: State = {
    isLoading: true,
    detailData: {},
    isOpenAssign: false,
    isOpenReply: false,
    isOpenRemark: false,
    isFinish: false,
    isOpenType: false,
    currentReply: {},
    currentRemark: {},
  };

  // 全部问题分类
  private allTypeList: Record<string, any>[] = [];
  // 入口列表
  private sourcesList: InfoSelectOption[] = [];

  // 反馈信息Info
  private get feedbackInfo(): InfoItem[] {
    return [
      {
        label: '反馈ID',
        prop: 'id',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data, infoData) => (
            <div className={s.alignCenter}>
              <div>{data}</div>
              {infoData?.replyStatus === 0 && <div className={s.noReply}>未回复</div>}
              {infoData?.replyStatus === 1 && <div className={s.default}>已回复</div>}
              {infoData?.finaleEnd === 0 && <div className={s.noFinish}>未完结</div>}
              {infoData?.finaleEnd === 1 && <div className={s.default}>已完结</div>}
            </div>
          ),
        },
      },
      { label: '登记人', prop: 'applyUserName' },
      { label: '第三方用户昵称', prop: 'thirdNickName' },
      {
        label: '问题分类',
        prop: 'typeDescAll',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data) => (
            <div className={s.typeInfoItem}>
              <span>{data}</span>
              <Button
                className={s.xsmallButton}
                color="primary"
                size="xsmall"
                variant="outlined"
                onClick={() => this.setState({ isOpenType: true })}
              >修改
              </Button>
            </div>
          ),
        },
      },
      {
        label: '负责人',
        prop: 'ownerUser',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data, infoData) => (
            <span>
              <span>{infoData?.ownerUserName}</span>
              {data && infoData?.ownerUserName && <span>(</span>}
              {data && <span>{data}</span>}
              {data && infoData?.ownerUserName && <span>)</span>}
            </span>
          ),
        },
      },
      { label: '反馈内容', prop: 'content' },
      { label: '反馈设备',
        prop: 'device',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data, infoData) => (
            <>
              <span>{data}</span>
              {data && infoData?.sn && <span>-</span>}
              <span>{infoData?.sn}</span>
              {infoData?.sn && infoData?.cpu && <span>-</span>}
              <span>{infoData?.cpu}</span>
              {infoData?.deviceList?.find((item: Record<string, any>) => item.sn === infoData?.sn) && <span>（已绑定设备）</span>}
            </>
          ),
        } },
      { label: '大屏版本', prop: 'inuiVersion' },
      { label: '固件版本', prop: 'systemVersion' },
      { label: 'launcher版本', prop: 'launcherVersionCode' },
      { label: 'App版本', prop: 'appVersion' },
      { label: '手机系统', prop: 'phoneOs' },
      {
        label: '创建时间',
        prop: 'createdAt',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data) => <span>{data ? moment(data).format('YYYY-MM-DD HH:mm:ss') : data}</span>,
        },
      },
      {
        label: '来源',
        prop: 'source',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data) => (
            <InfoSelect
              data={data}
              options={[
                { label: 'APP', value: 0 },
                { label: 'H5', value: 1 },
                { label: '帖子', value: 2 },
                { label: 'CRM后台录入', value: 3 },
              ]}
            />
          ),
        },
      },
      {
        label: '入口',
        prop: 'subSource',
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data) => (
            <InfoSelect
              data={data}
              options={this.sourcesList}
            />
          ),
        },
      },
      {
        label: '附件',
        prop: 'imageList',
        getData: (data: Record<string, any>) => data?.imageList?.map((item: Record<string, any>) => item.url),
        type: InfoItemType.Custom,
        customProps: {
          showRender: (data) => {
            if (data) {
              // 简单判断下是视频还是图片
              const reg = /(?:mp4|avi|rm|rmvb|mov|wmv)$/;
              const images: string[] = [];
              const videos: string[] = [];
              data.forEach((item: string) => {
                if (reg.test(item)) {
                  videos.push(item);
                } else {
                  images.push(item);
                }
              });
              return (
                <>
                  <InfoFile type={InfoFileType.Image} data={images} />
                  <div className={s.videos}>
                    {videos.map((list: string, index: number) => (
                      <>
                        <span
                          className={s.video}
                          key={index}
                          onClick={() => { window.open(list); }}
                        >
                          <Icon type={icons.Videocam}/>
                          点击查看视频
                        </span>
                      </>
                    ))}
                  </div>
                </>
              );
            }
            return null;
          },
        },
      },
    ];
  }

  // 用户信息Info
  private userInfo: InfoItem[] = [
    { label: 'uid', prop: 'uid' },
    { label: '用户昵称', prop: 'nickName' },
    {
      label: '绑定电话',
      prop: 'mobilePhone',
      type: InfoItemType.Custom,
      customProps: {
        showRender: (data) => <a className={s.href} href={`tel:${data}`}>{data}</a>,
      },
    },
    {
      label: '绑定设备列表',
      prop: 'deviceList',
      type: InfoItemType.Custom,
      customProps: {
        showRender: (data) => (
          <>
            {data && data.map((item: Record<string, any>, index: number) => (
              <div className={s.deviceItem} key={index}>
                {item.sn && <span>{`串号-${item.sn}`}</span>}
                {item.sn && item.device && <span>,</span>}
                {item.device && <span>{`机型-${item.device}`}</span>}
                {item.device && item.cpu && <span>,</span>}
                {item.cpu && <span>{`平台-${item.cpu}`}</span>}
              </div>
            ))}
          </>
        ),
      },
    },
    { label: '大屏登录uid', prop: 'deviceUid' },
  ];

  // 回复信息Info
  private replyInfo: InfoItem[] = [
    {
      label: '',
      prop: 'replyList',
      type: InfoItemType.Custom,
      customProps: {
        showRender: (data) => (
          <div className={s.replyContainer}>
            <div className={s.flexEnd}>
              <Button
                className={s.xsmallButton}
                size="xsmall"
                color="primary"
                variant="outlined"
                onClick={() => this.setState({ isOpenReply: true, currentReply: {} })}
              >添加回复
              </Button>
            </div>
            {data && data.map((item: Record<string, any>, index: number) => (
              <div className={s.replyItem} key={index}>
                <div className={s.infoItem}>
                  <div className={s.commentHeader}>
                    <span>{moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
                    <span className={s.replyUser}>{item.replyUser}</span>
                  </div>
                  <Button
                    className={s.xsmallButton}
                    size="xsmall"
                    color="primary"
                    variant="outlined"
                    onClick={() => this.setState({ isOpenReply: true, currentReply: item })}
                  >修改回复
                  </Button>
                </div>
                <div className={s.replyContent}>{item.content}</div>
              </div>
            ))}
          </div>
        ),
      },
    },
  ];

  // 跟进信息Info
  private processInfo: InfoItem[] = [
    {
      label: '处理类型',
      prop: 'actionType',
      type: InfoItemType.Radio,
      radioProps: {
        isUpdate: true,
        isDeselect: false,
        options: [
          { label: '需求', value: 1 },
          { label: 'bug', value: 2 },
          { label: '咨询', value: 3 },
          { label: '无效', value: 4 },
          { label: '售后', value: 5 },
          { label: '其它', value: 6 },
          { label: '表扬', value: 7 },
        ],
        onUpdate: (data) => this.onProcess({ actionType: data }),
      },
    },
    {
      label: '跟进状态',
      prop: 'processStatusList',
      type: InfoItemType.Checkbox,
      checkboxProps: {
        isUpdate: true,
        options: [
          { label: '未处理', value: 0 },
          { label: '已联系', value: 1 },
          { label: '已回复', value: 2 },
          { label: '已完结', value: 3 },
          { label: '用户未回复', value: 4 },
          { label: '已复现', value: 5 },
          { label: '未复现', value: 6 },
        ],
        onUpdate: (data) => this.onProcess({ processStatus: data }),
      },
    },
    {
      label: '备注信息',
      prop: 'feedbackRemarkList',
      type: InfoItemType.Custom,
      customProps: {
        showRender: (data) => (
          <>
            <div className={s.flexEnd}>
              <Button
                className={s.xsmallButton}
                size="xsmall"
                color="primary"
                variant="outlined"
                onClick={() => {
                  this.setState({ isOpenRemark: true });
                }}
              >添加备注
              </Button>
            </div>
            {data && data.map((item: Record<string, any>, index: number) => (
              <div
                key={index}
                className={s.remarkContent}
              >
                <div className={s.remarkTime}>{`${moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss')} ${item.createdBy}`}</div>
                <div> { `备注：${item.remark}`}</div>
              </div>
            ))}
          </>
        ),
      },
    },
  ];

  constructor(props: Props) {
    super(props, { title: '意见反馈详情' });
  }

  componentDidMount() {
    this.getLoginInfo();
  }

  /**
   * 获取登录信息
   */
  private getLoginInfo = (): void => {
    new HttpStateLine(new Api.crmFeedback.GetLoginInfo({ code: getUrlParams().code }), {
      onSuccess: () => {
        // 登录成功了才执行后续的业务
        this.setState({ isLoading: true });
        this.getDetail();
        this.getAllTypeList();
        this.getAllSources();
      },
    }).run();
  };

  /**
   * 获取反馈详情
   */
  private getDetail = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.crmFeedback.GetDetail({
      feedbackNo: this.props.match.params.feedbackNo,
    })).run().catch(() => null);
    this.setState({ detailData: httpStateLine?.result?.data || {}, isLoading: false });
  };

  /**
   * 获取所有类型列表
   */
  private getAllTypeList = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.crmFeedback.GetTypeList()).run().catch(() => null);
    this.allTypeList = httpStateLine?.result?.data || [];
  };

  /**
   * 获取所有入口
   */
  private getAllSources = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.crmFeedback.QuerySubSources({})).run().catch(() => null);
    this.sourcesList = httpStateLine?.result?.data?.subSources.map((item: Record<string, any>) => ({ label: item.desc, value: item.code } || []));
  };

  /**
   * 点击完结
   */
  private onFinish = (): void => {
    this.setState({ isFinish: false });
    new HttpStateLine(new Api.crmFeedback.Finish({
      feedbackNo: this.state.detailData.feedbackNo,
    }), {
      onSuccess: () => {
        this.storeState.common.toast.current?.show({ content: '操作成功' });
        this.getDetail();
      },
      onFail: (httpStateLine) => {
        this.storeState.common.toast.current?.show({ content: httpStateLine.result?.message });
      },
    }).run();
  };

  /**
   * 修改分类
   */
  private onTypeChange = (data: Record<string, any>): void => {
    new HttpStateLine(new Api.crmFeedback.AssignFeedback({
      feedbackNo: this.state.detailData.feedbackNo,
      ...data,
    }), {
      onSuccess: () => {
        this.storeState.common.toast.current?.show({ content: '修改成功' });
        this.getDetail();
      },
      onFail: (httpStateLine) => {
        this.storeState.common.toast.current?.show({ content: httpStateLine.result?.message });
      },
    }).run();
  };

  /**
   * 处理
   * @param data 参数
   */
  private onProcess = (data: Record<string, any>): void => {
    new HttpStateLine(new Api.crmFeedback.SaveDeal({
      feedbackNo: this.state.detailData.feedbackNo,
      ...data,
    }), {
      onSuccess: () => {
        this.storeState.common.toast.current?.show({ content: '操作成功' });
        this.getDetail();
      },
      onFail: (httpStateLine) => {
        this.storeState.common.toast.current?.show({ content: httpStateLine.result?.message });
      },
    }).run();
  };

  render(): React.ReactNode {
    return (
      <Container className={s.container} isMobile={true}>
        {this.state.isLoading ? <div className={s.progress}><CircularProgress size={20} /></div> : (
          <>
            <div className={s.accordion}>
              <Accordion title="反馈信息" isDefaultExpand={true}>
                <Info
                  className={s.info}
                  classes={{ label: s.infoLabel }}
                  list={this.feedbackInfo}
                  data={this.state.detailData}
                />
              </Accordion>
            </div>
            <div className={s.accordion}>
              <Accordion title="用户信息" isDefaultExpand={true}>
                <Info className={s.info} classes={{ label: s.infoLabel }} list={this.userInfo} data={this.state.detailData} />
              </Accordion>
            </div>
            {this.state.detailData?.source !== 3 && (
              <div className={s.accordion}>
                <Accordion title="回复信息" isDefaultExpand={true}>
                  <Info
                    className={s.replyInfo}
                    classes={{ label: s.infoLabel }}
                    list={this.replyInfo}
                    data={this.state.detailData}
                  />
                </Accordion>
              </div>
            )}
            <div className={s.accordion}>
              <Accordion title="跟进信息" isDefaultExpand={true}>
                <Info
                  className={s.info}
                  classes={{ label: s.infoLabel }}
                  list={this.processInfo}
                  data={this.state.detailData}
                />
              </Accordion>
            </div>
            <div className={s.accordion}>
              <Accordion title="操作记录" isDefaultExpand={true}>
                <div className={s.record}>
                  {this.state.detailData?.feedbackLogResList?.map((item: Record<string, any>, index: number) => (
                    <div className={s.recordItem} key={index}>
                      <div>{moment(item.actionTime).format('YYYY-MM-DD HH:mm:ss')}</div>
                      <div className={s.recordContent}>{item.content}</div>
                    </div>
                  ))}
                </div>
              </Accordion>
            </div>
            <div className={s.accordion}>
              <Accordion title="相关反馈" isDefaultExpand={true}>
                <Paper className={s.tableContainer} variant="outlined">
                  <Table
                    classes={{ emptyRoot: s.empty }}
                    columns={[
                      { title: 'ID',
                        prop: 'id',
                        minWidth: 80,
                        render: (data, rowData: Record<string, any>) => (
                          <span
                            className={s.href}
                            onClick={() => {
                              window.location.href = `${window.location.origin}/crm/feedback/detail/${rowData.feedbackNo}`;
                            }}
                          >{data}
                          </span>
                        ) },
                      {
                        title: '来源及入口',
                        prop: 'source',
                        minWidth: 250,
                        render: (data, rowData: Record<string, any>) => (
                          <div className={s.relateFeedback}>
                            <InfoSelect
                              data={data}
                              options={[
                                { label: 'APP', value: 0 },
                                { label: 'H5', value: 1 },
                                { label: '帖子', value: 2 },
                                { label: '手动录入', value: 3 },
                              ]}
                            />
                            <span className={s.divider}>/</span>
                            <InfoSelect
                              data={rowData.subSource}
                              options={this.sourcesList}
                            />
                          </div>
                        ),
                      },
                      { title: '机型', prop: 'device', minWidth: 100 },
                      { title: '反馈内容', prop: 'content', minWidth: 250 },
                      { title: '用户昵称', prop: 'nickName', minWidth: 100 },
                      {
                        title: '回复状态',
                        prop: 'replyStatus',
                        minWidth: 100,
                        render: (data) => (
                          <InfoSelect
                            options={[
                              { label: '未回复', value: 0, color: red[500] },
                              { label: '已回复', value: 1, color: green[500] },
                            ]}
                            data={data}
                          />
                        ),
                      },
                      {
                        title: '创建时间',
                        prop: 'createdAt',
                        minWidth: 180,
                        render: (data) => <span>{data ? moment(data).format('YYYY-MM-DD HH:mm:ss') : data}</span>,
                      },
                    ]}
                    data={this.state.detailData?.associatedFeedbackList}
                  />
                </Paper>
              </Accordion>
            </div>

            {/* 指派 */}
            <Assign
              data={this.state.detailData}
              isOpen={this.state.isOpenAssign}
              onClose={() => this.setState({ isOpenAssign: false })}
              onSuccess={() => {
                this.setState({ isOpenAssign: false });
                this.getDetail();
              }}
            />

            {/* 回复 */}
            <Reply
              data={this.state.detailData}
              replyData={this.state.currentReply}
              isOpen={this.state.isOpenReply}
              onClose={() => this.setState({ isOpenReply: false })}
              onSuccess={() => {
                this.setState({ isOpenReply: false });
                this.getDetail();
              }}
            />

            {/* 备注 */}
            <Remark
              data={this.state.detailData}
              isOpen={this.state.isOpenRemark}
              onClose={() => this.setState({ isOpenRemark: false })}
              onSuccess={() => {
                this.setState({ isOpenRemark: false });
                this.getDetail();
              }}
            />

            {/* 完结二次确认 */}
            <Dialog isOpen={this.state.isFinish} isResponsive={false} isAutoClose={false}>
              <Confirm
                confirmText="确认"
                cancelText="取消"
                onConfirm={this.onFinish}
                onCancel={() => this.setState({ isFinish: false })}
              >
                是否确认完结该条反馈？
              </Confirm>
            </Dialog>

            {/* 选择反馈类型 */}
            <Type
              open={this.state.isOpenType}
              onUpdate={(typeCode) => {
                this.onTypeChange({ feedbackTypeCode: typeCode });
              }}
              onClose={() => this.setState({ isOpenType: false })}
              data={this.allTypeList}
            />

            {/* 跳转反馈列表 */}
            <Fab
              className={s.back}
              color="default"
              onClick={() => {
                // 把当前最新的数据缓存起来用于列表的更新
                window.localStorage.setItem('crm_detail', JSON.stringify({
                  finaleEnd: this.state.detailData.finaleEnd,
                  replyStatus: this.state.detailData.finaleEnd,
                  feedbackTypeAll: this.state.detailData.typeDescAll,
                  processStatus: this.state.detailData.processStatusList.map((item: number) => ['未处理', '已联系', '已回复', '已完结', '用户未回复', '已复现', '未复现'][item]).toString(),
                }));
                this.history.push(`/crm/feedback/list${window.location.search}`);
              }}
            >反馈列表
            </Fab>

            <Paper className={s.bottomBar} elevation={5}>
              <div>
                {!this.state.detailData.firstReplyDate && <div className={s.planTime}>期望处理时间: {moment(this.state.detailData.planReplyDate).format('YYYY-MM-DD HH:mm')}</div>}
                {this.state.detailData.firstReplyDate && <div className={s.planTime}>首次处理时间: {moment(this.state.detailData.firstReplyDate).format('YYYY-MM-DD HH:mm')}</div>}
                <div className={s.delayTime}>{this.state.detailData.overtime}</div>
              </div>
              {this.state.detailData.finaleEnd === 0 && (
              <div>
                <Button className={s.assignButton} color="primary" disableElevation={true} onClick={() => this.setState({ isOpenAssign: true })}>指派</Button>
                <Button color="error" disableElevation={true} onClick={() => this.setState({ isFinish: true })}>完结</Button>
              </div>
              )}
            </Paper>
          </>
        )}
      </Container>
    );
  }
}

export default connect(mapStateToProps)(Component);
